import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ContentBlock from '@/components/molecules/ContentBlock';
import type { LaksvelParameterEntry, WoundsData } from '@/domain/smoltReport';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import { laksvelParameterLabel } from '../utils/laksvelParameterLabel';
import {
    mapLaksvelDevelopmentDatasets,
    mapLaksvelScoreItems,
    mapWoundsDevelopmentDatasets,
    mapWoundsTypeItems,
} from '../utils/welfareMappers';

import WelfareCard from './WelfareCard';

import styles from './WelfareSection.module.scss';

interface WoundsCardProps {
    wounds: WoundsData;
    isLoading: boolean;
    isPdfExport: boolean;
}

/** Acute and healing wounds, the report's body-wounds parameter. */
export function WoundsCard(props: WoundsCardProps) {
    const { wounds, isLoading, isPdfExport } = props;
    const { t } = useTranslation();

    const items = useMemo(() => mapWoundsTypeItems(wounds, t), [wounds, t]);
    const datasets = useMemo(() => mapWoundsDevelopmentDatasets(wounds, t), [wounds, t]);

    return (
        <ContentBlock
            spacing="none"
            title={t('smoltReport.detailView.woundsTitle')}
            headerActions={<NumberOfMeasurementsLabel numMeasurements={wounds.numMeasurements} />}>
            <WelfareCard
                barsTitle={t('smoltReport.detailView.woundsTypeTitle')}
                barsItems={items}
                chartDatasets={datasets}
                isLoading={isLoading}
                decimals={2}
                isPdfExport={isPdfExport}
            />
        </ContentBlock>
    );
}

interface LaksvelParameterCardProps {
    entry: LaksvelParameterEntry;
    isLoading: boolean;
    isPdfExport: boolean;
}

/** One Laksvel score parameter: its score shares and their development. */
export function LaksvelParameterCard(props: LaksvelParameterCardProps) {
    const { entry, isLoading, isPdfExport } = props;
    const { t } = useTranslation();

    const items = useMemo(() => mapLaksvelScoreItems(entry, t), [entry, t]);
    const datasets = useMemo(() => mapLaksvelDevelopmentDatasets(entry, t), [entry, t]);

    return (
        <ContentBlock
            spacing="none"
            title={laksvelParameterLabel(t, entry.parameter)}
            headerActions={<NumberOfMeasurementsLabel numMeasurements={entry.numMeasurements} />}>
            <WelfareCard
                barsTitle={t('smoltReport.detailView.laksvelScoreTitle')}
                barsItems={items}
                chartDatasets={datasets}
                isLoading={isLoading}
                isPdfExport={isPdfExport}
            />
        </ContentBlock>
    );
}

interface NumberOfMeasurementsLabelProps {
    numMeasurements: number;
}

function NumberOfMeasurementsLabel(props: NumberOfMeasurementsLabelProps) {
    const { numMeasurements } = props;
    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    const numberOfMeasurementsLabel = useMemo(() => {
        return `${t('smoltReport.detailView.numberOfMeasurements')} ${formatNumberByLocale(numMeasurements, locale, 0)}`;
    }, [numMeasurements, locale, t]);
    return <div className={styles.numberOfMeasurementsLabel}>{numberOfMeasurementsLabel}</div>;
}
